import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  Image,
  Link as ChakraLink,
  Heading,
  Grid,
  Flex,
  Box,
} from "@chakra-ui/react";
import { useLocation, useNavigate } from "react-router-dom";
import Icon from "../../../components/Icon";

import { FC } from "react";
import InAppCredits from "./InAppCredits";
import CloseBtn from "../../../pages/Website/layout/components/CloseBtn";
import { websiteSections } from "../../../utils/website.sections";
import { icons } from "../../../components/Icon/iconsList";
import { ClientStorage, ClientStorageItem } from "../../../utils/clientstorage";
import { UserWeddingRole } from "../../../types/wedding";
import { useSnapshot } from "valtio";
import { editorState } from "../../../utils/editor";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag";
import { Routes } from "../../../routes/baseRoutes";
import { getCoupleDisplayName } from "../../../utils/couple.names";

type MobileNavProps = {
  shouldDisplayCatering: boolean;
  isOpen: boolean;
  onClose: () => void;
};

const MobileNavDrawer: FC<MobileNavProps> = ({
  isOpen,
  onClose,
  shouldDisplayCatering,
}) => {
  const { enabled: designStudioEnabled } = useFeatureFlag("design_studio");
  const { pathname } = useLocation();
  const isWebsiteFlow = pathname.includes("/website");
  const isCateringFlow = pathname.includes("/catering");
  const isGuestFlow = pathname.includes("/guest");

  const moduleRanking = {
    website: isWebsiteFlow ? 0 : 1,
    catering: isCateringFlow ? 0 : 1,
    guest: isGuestFlow ? 0 : 1,
  };

  const navigate = useNavigate();
  const loc = useLocation();
  const path = loc.pathname;

  const userWeddingRole = ClientStorage.fetch(
    ClientStorageItem.UserWeddingRole,
  ) as UserWeddingRole;

  const isEventPlanner = userWeddingRole === UserWeddingRole.EventPlanner;
  const isEventPlannerPath = pathname.startsWith("/planner/weddings/");
  const isEventPlannerInWedding = isEventPlanner && isEventPlannerPath;

  const basePath = isEventPlannerInWedding ? pathname.match(/^\/planner\/weddings\/[^/]+/)?.[0] || "" : "";

  const snap = useSnapshot(editorState);
  const imageInfoText = getCoupleDisplayName(snap);

  type NavBtnProps = {
    url: string;
    iconProps: [keyof typeof icons, string]; // tuple of name and variant
    text: string;
  };
  const NavBtn: FC<NavBtnProps> = ({ url, iconProps, text }) => {
    const [name, variant] = iconProps;
    const isActive = path === url;
    return (
      <Flex
        backgroundColor={isActive ? "rgba(46, 86, 59, 1)" : "#fff"}
        color={isActive ? "#fff" : "#000"}
        padding={"8px 16px"}
        transition={{ backgroundColor: "100ms" }}
        display={"flex"}
        borderRadius={"64px"}
        columnGap={"10px"}
        alignItems={"center"}
        width={"fit-content"}
        onClick={() => {
          navigate(url);
          onClose();
        }}
      >
        <Icon name={name} variant={variant} height={"20px"} width={"20px"} />
        {text}
      </Flex>
    );
  };

  return (
    <Drawer
      isOpen={isOpen}
      placement="left"
      onClose={onClose}
      variant={"mobileNav"}
      closeOnOverlayClick={true}
    >
      <DrawerOverlay />
      <DrawerContent
        bg={"#fff"}
        color={"#000"}
        className={"no-scrollbar mobileNav"}
        fontFamily={"font4"}
        p={{ base: "0.675rem", md: "1rem" }}
        pos={"relative"}
      >
        <Box pos={"absolute"} top={"1rem"} right={"1rem"}>
          <CloseBtn onClose={onClose} />
        </Box>
        <Box
          as="nav"
          maxW={"280px"}
          h={"auto"}
          overflow={"auto"}
          className={"no-scrollbar"}
          sx={{
            "> * + *": {
              marginTop: "20px",
            },
          }}
        >
          <Box>
            <Image
              src={"/icons/weddnlogo.svg"}
              alt="logo"
              w={{ base: "120px", md: "160px" }}
            />
          </Box>
          <NavBtn
            url={"/"}
            iconProps={["dashboard", "stroke"]}
            text={isEventPlanner ? "All Weddings " : "Dashboard "}
          />
          {!isEventPlanner && (
            <NavBtn
              url={Routes.myWeddn}
              iconProps={["checkMark", "stroke"]}
              text="My Weddn"
            />
          )}
          {isEventPlanner && isEventPlannerInWedding ? (
            <Box mt={0}>
              <NavBtn
                url={basePath}
                iconProps={["dashboard", "stroke"]}
                text={imageInfoText}
              />
            </Box>
          ) : null}
          {isEventPlanner && !isEventPlannerInWedding ? null : (
            <Flex flexDir={"column"} gap={"1rem"}>
              <Box>
                <Heading
                  as="h3"
                  fontSize="0.765rem"
                  fontWeight={600}
                  textTransform="uppercase"
                >
                  Wedding tools
                </Heading>
                <Grid rowGap="5px">
                  {!isEventPlanner && (
                    <NavBtn
                      url={Routes.messaging}
                      iconProps={["envelope", "stroke"]}
                      text="Bulk Messaging"
                    />
                  )}
                  <NavBtn
                    url={basePath + "/aso-ebi"}
                    iconProps={["goodStatus", "stroke"]}
                    text="Aso Ebi Store"
                  />
                  <NavBtn
                    url={basePath + "/love-wall"}
                    iconProps={["lovewall", "stroke"]}
                    text="Love Wall"
                  />
                </Grid>
              </Box>
              {shouldDisplayCatering && (
                <Box order={moduleRanking.catering}>
                  <Heading
                    as={"h3"}
                    fontSize={"0.765rem"}
                    fontWeight={600}
                    textTransform={"uppercase"}
                  >
                    {" "}
                    Catering{" "}
                  </Heading>
                  <Grid>
                    <NavBtn
                      url={basePath + "/catering"}
                      iconProps={["catering", "stroke"]}
                      text="Manage"
                    />
                  </Grid>
                </Box>
              )}
              <Box order={moduleRanking.guest}>
                <Heading
                  as={"h3"}
                  fontSize={"0.765rem"}
                  fontWeight={600}
                  textTransform={"uppercase"}
                >
                  {" "}
                  Guest{" "}
                </Heading>
                <Grid rowGap={"5px"}>
                  <NavBtn
                    url={basePath + "/guests"}
                    iconProps={["people", "stroke"]}
                    text="Manage Guests"
                  />
                  <NavBtn
                    url={basePath + "/guests/wellwishes"}
                    iconProps={["wellWishes", "stroke"]}
                    text="Well Wishes"
                  />
                  <NavBtn
                    url={basePath + "/guests/tableplan"}
                    iconProps={["tablePlan", "fill"]}
                    text="Table Plan"
                  />
                  <NavBtn
                    url={basePath + "/guests/accesscards"}
                    iconProps={["access", "stroke"]}
                    text="Access Card"
                  />
                  <NavBtn
                    url={basePath + "/guests/messaging"}
                    iconProps={["envelope", "stroke"]}
                    text="Guest Messaging"
                  />
                  <NavBtn
                    url={basePath + "/guests/preference"}
                    iconProps={["coin", "stroke"]}
                    text="Preferences"
                  />
                </Grid>
              </Box>

              <Box order={moduleRanking.website}>
                <Heading
                  as={"h3"}
                  fontSize={"0.765rem"}
                  fontWeight={600}
                  textTransform={"uppercase"}
                >
                  {" "}
                  Website{" "}
                </Heading>
                <Grid>
                  <NavBtn
                    url={basePath + "/website"}
                    iconProps={["refresh", "stroke"]}
                    text="Overview"
                  />
                  <NavBtn
                    url={basePath + "/website/design"}
                    iconProps={["edit", "stroke"]}
                    text="Design"
                  />
                  {websiteSections(basePath).map((section, index) => (
                    <NavBtn
                      key={index + section.url}
                      url={section.url}
                      iconProps={[section.icon!, "stroke"]}
                      text={
                        section.slug === "aso-ebi"
                          ? "Website Aso Ebi"
                          : section.title
                      }
                    />
                  ))}
                  <NavBtn
                    url={basePath + "/website?view=dday"}
                    iconProps={["bigDay", "stroke"]}
                    text="Big Day"
                  />
                  <ChakraLink className="mobileNav__item">
                    <Icon name="gallery" variant="stroke" />
                    Memories <span className="comingSoon"> Coming soon </span>
                  </ChakraLink>
                </Grid>
              </Box>
              {designStudioEnabled && <Box>
                <Heading
                  as={"h3"}
                  fontSize={"0.765rem"}
                  fontWeight={600}
                  textTransform={"uppercase"}
                >
                  Designs
                </Heading>
                <Grid>
                  <NavBtn
                    url={basePath + "/design"}
                    iconProps={["edit", "stroke"]}
                    text="Design Studio"
                  />
                </Grid>
              </Box>}
            </Flex>
          )}

          {isEventPlanner && !isEventPlannerInWedding ? null : <InAppCredits />}
        </Box>
      </DrawerContent>
    </Drawer>
  );
};

export default MobileNavDrawer;
